import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { getLscLiveReadiness } from '../api';
import { lscPath } from '../routing';
import type { LscLiveReadinessReport } from '../types';
import LiveDetectionPanel from './LiveDetectionPanel';
import {
  informationalWarnings,
  liveReadinessStatusBody,
  liveReadinessStatusHeading,
  resolveLiveReadinessDisplayState,
  uniqueSetupIssueLabels,
  type LiveReadinessDisplayState,
} from './liveReadinessDisplay';

export type LiveReadinessCardProps = {
  leagueId: string;
  isOwner?: boolean;
  onReadinessRefresh?: () => void;
};

function formatBlockerLabel(code: string): string {
  return code.replace(/_/g, ' ');
}

function StatusIcon({ displayState }: { displayState: LiveReadinessDisplayState }) {
  if (displayState === 'live_detected' || displayState === 'ready') {
    return <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" aria-hidden />;
  }
  if (displayState === 'warning') {
    return <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" aria-hidden />;
  }
  return <XCircle className="h-5 w-5 text-red-400 shrink-0" aria-hidden />;
}

function statusBannerClass(displayState: LiveReadinessDisplayState): string {
  if (displayState === 'live_detected' || displayState === 'ready') {
    return 'border-emerald-500/30 bg-emerald-500/10';
  }
  if (displayState === 'warning') {
    return 'border-amber-500/40 bg-amber-500/10';
  }
  return 'border-secondary-700/60 bg-secondary-900/40';
}

export function LiveReadinessCardView({
  readiness,
  loading,
  error,
  onRefresh,
  children,
}: {
  readiness: LscLiveReadinessReport | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  children?: React.ReactNode;
}) {
  const [showCandidates, setShowCandidates] = useState(false);
  const displayState = readiness ? resolveLiveReadinessDisplayState(readiness) : 'blocked';
  const setupNotices =
    readiness && displayState === 'live_detected'
      ? uniqueSetupIssueLabels(readiness.blockers)
      : [];
  const secondaryWarnings = readiness ? informationalWarnings(readiness.warnings) : [];

  return (
    <div className="card space-y-4" data-testid="live-readiness-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Live game readiness</h2>
          <p className="text-sm text-secondary-400 mt-1">
            Preflight checks before a member goes live. Runtime live detection takes precedence once
            a qualified league game is live.
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="btn-secondary inline-flex items-center gap-2 text-sm shrink-0"
          aria-label="Refresh live readiness"
          data-testid="live-readiness-refresh"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="live-readiness-loading">
          Checking live game readiness...
        </p>
      ) : error ? (
        <div className="space-y-3">
          <p className="text-sm text-red-300" data-testid="live-readiness-error">
            {error}
          </p>
          <button type="button" className="btn-secondary text-sm" onClick={onRefresh}>
            Retry
          </button>
        </div>
      ) : readiness ? (
        <>
          <div
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${statusBannerClass(displayState)}`}
            data-testid="live-readiness-status-banner"
            data-display-state={displayState}
          >
            <StatusIcon displayState={displayState} />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-white" data-testid="live-readiness-status-heading">
                {liveReadinessStatusHeading(displayState)}
              </p>
              <p className="text-secondary-400" data-testid="live-readiness-status-body">
                {liveReadinessStatusBody(displayState, readiness)}
              </p>
            </div>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-secondary-500">Open matches (current stage)</dt>
              <dd className="text-secondary-100">{readiness.current_open_matches_count}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Members ready for live detection</dt>
              <dd className="text-secondary-100">{readiness.eligible_live_game_members_count}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Live detection ready</dt>
              <dd className="text-secondary-100">
                {readiness.eventsub_subscription_status.members_with_enabled_pair} /{' '}
                {readiness.eventsub_subscription_status.active_members_with_twitch}
              </dd>
            </div>
            <div>
              <dt className="text-secondary-500">Active stage</dt>
              <dd className="text-secondary-100">{readiness.active_stage_label || '—'}</dd>
            </div>
          </dl>

          {setupNotices.length > 0 ? (
            <div data-testid="live-readiness-setup-notices">
              <p className="text-sm font-medium text-amber-200 mb-1">Live detection setup</p>
              <ul className="list-disc list-inside text-sm text-amber-100/90 space-y-1">
                {setupNotices.map((notice) => (
                  <li key={notice}>{notice}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {readiness.blockers.length > 0 && displayState !== 'live_detected' ? (
            <div data-testid="live-readiness-blockers">
              <p className="text-sm font-medium text-red-200 mb-1">Blockers</p>
              <ul className="list-disc list-inside text-sm text-red-100/90 space-y-1">
                {readiness.blockers.map((blocker) => (
                  <li key={blocker}>{formatBlockerLabel(blocker)}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {secondaryWarnings.length > 0 ? (
            <div data-testid="live-readiness-warnings">
              <p className="text-sm font-medium text-amber-200 mb-1">Warnings</p>
              <ul className="list-disc list-inside text-sm text-amber-100/90 space-y-1">
                {secondaryWarnings.map((warning) => (
                  <li key={warning}>{formatBlockerLabel(warning)}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {readiness.live_state_status.status === 'idle' &&
          readiness.live_state_status.qualified_live_games_count === 0 ? (
            <p className="text-sm text-secondary-500" data-testid="live-readiness-idle-note">
              No league members are live right now.
            </p>
          ) : null}

          {readiness.next_actions.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="live-readiness-next-actions">
              {readiness.next_actions.map((action) =>
                action.code === 'sync_live_subscriptions' ? (
                  <a
                    key={action.code}
                    href="#live-detection"
                    className="btn-secondary text-sm"
                  >
                    {action.label}
                  </a>
                ) : (
                  <Link
                    key={action.code}
                    to={lscPath(action.href_path.split('#')[0])}
                    className="btn-secondary text-sm"
                  >
                    {action.label}
                  </Link>
                )
              )}
            </div>
          ) : null}

          {readiness.test_candidate_members.length > 0 ? (
            <div>
              <button
                type="button"
                className="text-sm text-primary-300 hover:text-primary-200"
                onClick={() => setShowCandidates((value) => !value)}
                data-testid="live-readiness-toggle-candidates"
              >
                {showCandidates ? 'Hide' : 'Show'} test candidate members (
                {readiness.test_candidate_members.length})
              </button>
              {showCandidates ? (
                <ul
                  className="mt-3 space-y-2 text-sm"
                  data-testid="live-readiness-candidate-list"
                >
                  {readiness.test_candidate_members.map((member) => (
                    <li
                      key={member.member_id || member.display_name || 'unknown'}
                      className="rounded border border-secondary-700/60 px-3 py-2"
                    >
                      <p className="text-secondary-100 font-medium">
                        {member.display_name || member.member_id}
                      </p>
                      <p className="text-secondary-400">
                        {member.readiness_status} · {member.open_match_count} open match(es) · Live
                        detection {member.has_eventsub_subscription ? 'ready' : 'missing'}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {children}
        </>
      ) : null}
    </div>
  );
}

const LiveReadinessCard: React.FC<LiveReadinessCardProps> = ({
  leagueId,
  isOwner = true,
  onReadinessRefresh,
}) => {
  const [readiness, setReadiness] = useState<LscLiveReadinessReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadReadiness = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getLscLiveReadiness(leagueId);
      setReadiness(next);
    } catch {
      setError('Live readiness unavailable.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadReadiness();
  }, [loadReadiness]);

  return (
    <LiveReadinessCardView
      readiness={readiness}
      loading={loading}
      error={error}
      onRefresh={() => {
        void loadReadiness();
      }}
    >
      <LiveDetectionPanel
        leagueId={leagueId}
        readiness={readiness}
        readinessLoading={loading}
        onRefreshReadiness={loadReadiness}
        canRepairAlerts={isOwner}
        onMutationSuccess={onReadinessRefresh}
      />
    </LiveReadinessCardView>
  );
};

export default LiveReadinessCard;
